import { Box, Button, CircularProgress, Snackbar, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { Link } from 'react-router-dom';
import { LinkPageData, LinkPageOutlink } from 'common-ts/dist/models/LinkPage';
import { ProductOnUser } from 'common-ts/dist/models/Product';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import {
  fetchLinkPage, fetchProductsByIds, TERBEBAS_STORE_SLUG,
} from '../terbebasApi';
import { useAppSelector } from '../reduxhooks';
import { SRS_HTTP_URL } from '../App';
import LinkPageProductCarousel from './LinkPageProductCarousel';
import LinkPageOutlinkList from './LinkPageOutlinkList';
import LinkPageExitDialog from './LinkPageExitDialog';
import LinkPageVariantSheet from './LinkPageVariantSheet';
import LinkPageOngkirDialog from './LinkPageOngkirDialog';
import LinkPageLivePlayer from './LinkPageLivePlayer';
import {
  hargaLangsung, marketplaceComparison, RegionPrefill, useLinkPageBuyNow,
} from './LinkPageShared';

// The /link "link in bio" page: minimal-chrome, mobile-first landing page
// whose top segment (livestream or price-comparison carousel) tries to
// convert marketplace-bound visitors into direct buyers before they leave
// through the outlinks below.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  root: {
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: '28px 18px 40px',
  },
  inner: {
    maxWidth: '480px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  avatar: {
    width: '76px',
    height: '76px',
    borderRadius: '50%',
    objectFit: 'cover',
    marginBottom: '10px',
  },
  displayName: {
    fontSize: '19px',
    fontWeight: 700,
  },
  bio: {
    fontSize: '13px',
    color: '#6c6c6c',
    marginTop: '4px',
  },
  segment: {
    marginBottom: '22px',
  },
  bannerImage: {
    width: '100%',
    borderRadius: '14px',
    display: 'block',
  },
  ongkirButton: {
    display: 'block',
    margin: '2px auto 0px',
    fontSize: '12px',
    textTransform: 'none',
    textDecoration: 'underline',
  },
  footer: {
    textAlign: 'center',
    marginTop: '34px',
    fontSize: '11px',
    color: '#9a9a9a',
  },
  centerBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '14px',
  },
}),
);

export default function PageLinkPage() {
  const classes = useStyles();
  const buyNow = useLinkPageBuyNow();

  const [linkPage, setLinkPage] = React.useState<LinkPageData | null | undefined>(undefined);
  const [extraProducts, setExtraProducts] = React.useState<{ [id: string]: ProductOnUser }>({});
  const [livePlaybackFailed, setLivePlaybackFailed] = React.useState(false);
  const [exitLink, setExitLink] = React.useState<LinkPageOutlink | null>(null);
  const [variantSheetProduct, setVariantSheetProduct] = React.useState<ProductOnUser | null>(null);
  const [ongkirOpen, setOngkirOpen] = React.useState(false);
  const [pendingPrefill, setPendingPrefill] = React.useState<RegionPrefill | undefined>(undefined);
  const [snackbarText, setSnackbarText] = React.useState('');

  const streamIsOn = useAppSelector((state) => state.livestreamState.streamIsOn);
  const storeProfile = useAppSelector((state) => state.storeProfile.profile);
  const productsDictionary = useAppSelector((state) => state.productsList.productsDictionary);
  const productsListStatus = useAppSelector((state) => state.productsList.status);

  const carouselRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    fetchLinkPage()
      .then((data) => setLinkPage(data))
      .catch(() => setLinkPage(null));
  }, []);

  // Hydrate carousel members the boot catalog didn't include (non-first-page
  // products), through the same by-ids path collections use.
  const productIdsKey = (linkPage?.topSegment.productIds ?? []).join(',');
  React.useEffect(() => {
    if (!linkPage || productsListStatus !== APIRequestStatus.Success) return;
    const missing = linkPage.topSegment.productIds.filter(
      (id) => !productsDictionary[id] && !extraProducts[id]
    );
    if (!missing.length) return;
    fetchProductsByIds(missing).then((products) => {
      if (!products.length) return;
      setExtraProducts((prev) => {
        const next = { ...prev };
        for (const product of products) next[product.productId] = product;
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdsKey, productsListStatus, linkPage]);

  if (linkPage === undefined) {
    return (
      <Box className={classes.centerBox}>
        <CircularProgress />
      </Box>
    );
  }

  if (linkPage === null) {
    return (
      <Box className={classes.centerBox}>
        <Typography style={{ fontSize: '16px', fontWeight: 600 }}>
          Halaman tidak ditemukan
        </Typography>
        <Button component={Link} to="/" variant="outlined">
          Ke Beranda
        </Button>
      </Box>
    );
  }

  const { topSegment, outlinks, theme: pageTheme } = linkPage;

  const carouselProducts: ProductOnUser[] = topSegment.productIds
    .map((id) => (productsDictionary as { [id: string]: ProductOnUser })[id] ?? extraProducts[id])
    .filter((p): p is ProductOnUser => !!p && p.showToUser !== false);

  // The concrete example for the exit dialog: the cheapest product with an
  // honest (higher-marketplace) comparison.
  const comparisonProduct = carouselProducts
    .filter((p) => marketplaceComparison(p) !== null)
    .sort((a, b) => hargaLangsung(a) - hargaLangsung(b))[0] ?? null;

  const displayName = pageTheme.displayName || storeProfile?.name || TERBEBAS_STORE_SLUG;
  const avatarUrl = pageTheme.avatarUrl || storeProfile?.config?.logoUrl || null;

  // Buy-now entry: single-variant products buy immediately; products with
  // options open the variant sheet first. regionPrefill (from the ongkir
  // dialog) rides along either way.
  const startBuyFlow = (product: ProductOnUser, prefill?: RegionPrefill) => {
    const commerce = product.commerce;
    if (!commerce || !commerce.variants.length) return;
    if (commerce.options.length > 0 && commerce.variants.length > 1) {
      setPendingPrefill(prefill);
      setVariantSheetProduct(product);
      return;
    }
    buyNow(product, commerce.variants[0], prefill).then((ok) => {
      if (!ok) setSnackbarText('Stok habis');
    });
  };

  const renderCarouselSegment = () => (
    <div ref={carouselRef}>
      <LinkPageProductCarousel
        products={carouselProducts}
        onBuyProduct={(product) => startBuyFlow(product)}
      />
      {
        carouselProducts.length > 0 &&
        <Button
          className={classes.ongkirButton}
          onClick={() => setOngkirOpen(true)}
        >
          Cek Ongkir ke Daerahmu
        </Button>
      }
    </div>
  );

  const renderBannerSegment = () => (
    topSegment.bannerImageUrl
      ? <img className={classes.bannerImage} src={topSegment.bannerImageUrl} alt={displayName} />
      : null
  );

  const renderTopSegment = () => {
    if (topSegment.template === 'livestream') {
      if (streamIsOn && !livePlaybackFailed) {
        return (
          <LinkPageLivePlayer
            url={`${SRS_HTTP_URL}/rtc/v1/whep/?app=live&stream=${TERBEBAS_STORE_SLUG}`}
            onPlaybackFailed={() => setLivePlaybackFailed(true)}
          />
        );
      }
      // Offline (or failed) fallback keeps the conversion pressure on.
      return topSegment.fallback === 'banner' ? renderBannerSegment() : renderCarouselSegment();
    }
    return renderCarouselSegment();
  };

  return (
    <Box
      className={classes.root}
      style={{
        backgroundColor: pageTheme.backgroundColor || '#fafafa',
        color: pageTheme.textColor || undefined,
      }}
    >
      <Box className={classes.inner}>
        <Box className={classes.header}>
          {avatarUrl && <img className={classes.avatar} src={avatarUrl} alt={displayName} />}
          <Typography className={classes.displayName}>{displayName}</Typography>
          {pageTheme.bio && <Typography className={classes.bio}>{pageTheme.bio}</Typography>}
        </Box>
        <Box className={classes.segment}>
          {renderTopSegment()}
        </Box>
        <LinkPageOutlinkList
          outlinks={outlinks}
          textColor={pageTheme.textColor}
          onMarketplaceClick={(link) => setExitLink(link)}
        />
        <Typography className={classes.footer}>
          Dibuat dengan Terbebas
        </Typography>
      </Box>

      <LinkPageExitDialog
        link={exitLink}
        comparisonProduct={comparisonProduct}
        onClose={() => setExitLink(null)}
        onStay={() => {
          setExitLink(null);
          if (carouselProducts.length === 1) {
            startBuyFlow(carouselProducts[0]);
          } else {
            carouselRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />
      <LinkPageVariantSheet
        product={variantSheetProduct}
        onClose={() => { setVariantSheetProduct(null); setPendingPrefill(undefined); }}
        onBuyNow={(product, variant) => {
          setVariantSheetProduct(null);
          buyNow(product, variant, pendingPrefill).then((ok) => {
            if (!ok) setSnackbarText('Stok habis');
          });
          setPendingPrefill(undefined);
        }}
      />
      <LinkPageOngkirDialog
        open={ongkirOpen}
        onClose={() => setOngkirOpen(false)}
        product={carouselProducts[0] ?? null}
        onLanjutBeli={(product, prefill) => {
          setOngkirOpen(false);
          startBuyFlow(product, prefill);
        }}
      />
      <Snackbar
        open={!!snackbarText}
        autoHideDuration={2500}
        onClose={() => setSnackbarText('')}
        message={snackbarText}
      />
    </Box>
  );
}
